import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "@/App.css";
import RecallModal from "@/components/RecallModal";
import Admin from "@/pages/Admin";
import { Toaster } from "@/components/ui/sonner";
import { detectLang } from "@/i18n";
import { useWidgetConfig } from "@/lib/useWidgetConfig";

function isEmbed() {
  try {
    return new URLSearchParams(window.location.search).get("embed") === "1";
  } catch (e) {
    return false;
  }
}

function getParentOrigin() {
  // Prefer the shop origin passed explicitly by the loader script (deterministic,
  // browser-independent). Fall back to ancestorOrigins/referrer.
  try {
    const shop = new URLSearchParams(window.location.search).get("shop");
    if (shop) return new URL(shop).origin;
  } catch (e) {
    /* ignore */
  }
  try {
    const ao = window.location.ancestorOrigins;
    if (ao && ao.length) return ao[ao.length - 1];
  } catch (e) {
    /* ignore */
  }
  try {
    if (document.referrer) return new URL(document.referrer).origin;
  } catch (e) {
    /* ignore */
  }
  return null;
}

function EmbeddedRecall() {
  const [open, setOpen] = useState(true);
  const lang = detectLang();
  const config = useWidgetConfig();

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.style.background = "transparent";
    document.body.style.background = "transparent";
  }, [lang]);

  const handleClose = () => {
    setOpen(false);
    try {
      window.parent.postMessage({ type: "RECALL_WIDGET_CLOSE" }, "*");
    } catch (e) {
      /* ignore */
    }
  };

  // Domain restriction: when embedded, only allow the configured shop domain(s).
  const embedded = window.parent !== window;
  const allowed = config?.embed_allowed_origins || null;
  const parentOrigin = getParentOrigin();
  const blocked = embedded && allowed && parentOrigin && !allowed.includes(parentOrigin);

  return (
    <div className="font-sans" data-testid="embed-root">
      <Toaster position="top-center" />
      {!blocked && (
        <RecallModal open={open} onClose={handleClose} lang={lang} config={config} shopOrigin={parentOrigin} />
      )}
    </div>
  );
}

function Root() {
  // The widget (embedded via ?embed=1) is the only public-facing page.
  // Any other visit to the root is sent to the admin backend.
  if (isEmbed()) return <EmbeddedRecall />;
  return <Navigate to="/admin" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<Root />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
